/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */

import React, { useState, useEffect } from "react";
import { Ban, AlertTriangle, Loader2 } from "lucide-react";

const RejectDialog = ({ isOpen, onClose, onConfirm }) => {
  const [reason, setReason] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError("");
      setIsLoading(false);
    }
  }, [isOpen]);

  const handleClose = () => {
    if (!isLoading) {
      // Clear state when closing
      setReason("");
      setError("");
      onClose();
    }
  };

  const handleConfirm = async () => {
    if (isLoading) return;

    if (!reason.trim()) {
      setError("Rejection reason is mandatory.");
      return;
    }

    setIsLoading(true);
    try {
      await onConfirm(reason);
      // Clear state after successful confirmation
      setReason("");
      setError("");
    } catch (error) {
      setError("");
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card dark:bg-dark-surface rounded-2xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in-95 duration-200">
        <div className="p-6 text-center">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto mb-4 text-red-600 dark:text-red-400">
            <Ban size={24} />
          </div>
          <h3 className="text-xl font-bold text-text dark:text-dark-text mb-2">
            Reject Application?
          </h3>
          <p className="text-gray-500 dark:text-dark-muted text-sm mb-6">
            This action will move the caregiver to &apos;Rejected&apos; status.
            A notification will be sent.
          </p>

          <div className="text-left mb-6">
            <label className="block text-sm font-semibold text-gray-700 dark:text-dark-muted mb-2">
              Reason for Rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => {
                setReason(e.target.value);
                setError("");
              }}
              disabled={isLoading}
              rows={3}
              className={`w-full p-3 bg-accent dark:bg-dark-border border rounded-xl outline-none focus:ring-2 focus:ring-red-500 resize-none text-text dark:text-dark-text disabled:opacity-50 disabled:cursor-not-allowed ${
                error
                  ? "border-red-500"
                  : "border-gray-200 dark:border-dark-border"
              }`}
              placeholder="e.g., Incomplete documents..."
            />
            {error && (
              <p className="text-red-500 text-xs mt-1 flex items-center gap-1">
                <AlertTriangle size={12} /> {error}
              </p>
            )}
          </div>

          <div className="flex gap-3">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="flex-1 py-2.5 bg-accent dark:bg-dark-border text-text dark:text-dark-text rounded-xl font-medium hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className="flex-1 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Rejecting...
                </>
              ) : (
                "Reject"
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectDialog;
